import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useMemo } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  AuthorityTypeRequest,
  IAuthorityTypeDetail,
} from "services/KnowfullyAdminClient";

const { Form } = withTypes<AuthorityTypeRequest>();

type SubmissionAuthorityFormProps = {
  authorityTypeId: string;
  submissionAuthority?: IAuthorityTypeDetail;
  title: string;
  onCancel: () => void;
  onSubmit: () => void;
};

const SubmissionAuthorityForm = ({
  authorityTypeId,
  submissionAuthority,
  title,
  onCancel,
  onSubmit,
}: SubmissionAuthorityFormProps & FormProps<AuthorityTypeRequest>) => {
  const { formatMessage: t } = useIntl();
  const { submissionAuthorityService } = useGlobalContext();

  const init = useMemo(
    () =>
      pick(submissionAuthority ?? {}, [
        "name",
        "isUploadSubmissionStatusAvailable",
        "submissionCutOffDays",
      ]),
    [submissionAuthority],
  );

  const handleSubmit = async (values: AuthorityTypeRequest) => {
    await submissionAuthorityService.updateAuthorityType(
      authorityTypeId,
      values,
    );
    onSubmit();
  };

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={init}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Field name="name" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "nameLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="isUploadSubmissionStatusAvailable" type="checkbox">
                {({ input }) => (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Checkbox {...input} />}
                      label={t({ id: "uploadSubmissionStatusAvailableLabel" })}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="submissionCutOffDays" type="number" required>
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      type="number"
                      label={t({ id: "submissionCutOffDaysLabel" })}
                      InputLabelProps={{ required: true }}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Grid item xs={12}>
                  <Alert severity="error">{submitError}</Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={submitting}
              startIcon={submitting && <CircularProgress size={16} />}
            >
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default SubmissionAuthorityForm;
